import { useEffect, useState } from 'react';
import { isAxiosError } from 'axios';
import { Icon } from '@/components/Icon';
import { Spinner } from '@/components/Spinner';
import {
  fetchPermissionCatalog,
  type PermissionGroup,
  type RoleDetail,
  type SaveRolePayload,
} from '@/api/roles';
import { GROUP_ICONS } from './groupIcons';

/**
 * Форма роли — поля 1:1 старый Orchid RoleEditLayout (название и
 * идентификатор) плюс блок «Разрешения/привилегии» из RolePermissionLayout.
 * Набор галочек приходит с сервера из того же реестра прав, что и в старой
 * админке, поэтому новые права появляются здесь автоматически.
 *
 * readOnly — режим просмотра для админов без права на изменение ролей: поля
 * и галочки видны, но заблокированы.
 */
export function RoleForm({
  initial,
  readOnly = false,
  onSave,
  onCancel,
}: {
  initial?: RoleDetail;
  readOnly?: boolean;
  onSave: (payload: SaveRolePayload) => Promise<void>;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [slug, setSlug] = useState(initial?.slug ?? '');
  const [selected, setSelected] = useState<string[]>(initial?.permissions ?? []);
  const [groups, setGroups] = useState<PermissionGroup[] | null>(null);
  const [fieldErrors, setFieldErrors] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const invalid = (field: string) => (fieldErrors.includes(field) ? ' edit-field--invalid' : '');

  useEffect(() => {
    let cancelled = false;
    fetchPermissionCatalog()
      .then((list) => {
        if (!cancelled) setGroups(list);
      })
      .catch(() => {
        if (!cancelled) setError('Не удалось загрузить список прав.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const toggle = (permissionSlug: string) => {
    setSelected((prev) =>
      prev.includes(permissionSlug)
        ? prev.filter((s) => s !== permissionSlug)
        : [...prev, permissionSlug],
    );
  };

  const toggleGroup = (group: PermissionGroup, checked: boolean) => {
    const slugs = group.permissions.map((p) => p.slug);
    setSelected((prev) =>
      checked ? [...new Set([...prev, ...slugs])] : prev.filter((s) => !slugs.includes(s)),
    );
  };

  const submit = async () => {
    const missing: string[] = [];
    if (!name.trim()) missing.push('name');
    if (!slug.trim()) missing.push('slug');

    if (missing.length > 0) {
      setFieldErrors(missing);
      setError('Заполните обязательные поля.');
      return;
    }

    // Идентификатор попадает в permissions и в URL — кириллица в нём
    // недопустима (сервер проверяет это же правило).
    if (!/^[A-Za-z0-9._-]+$/.test(slug.trim())) {
      setFieldErrors(['slug']);
      setError('Идентификатор может содержать только латиницу, цифры, точку, дефис и подчёркивание.');
      return;
    }

    setFieldErrors([]);

    setError(null);
    setSaving(true);
    try {
      await onSave({ name: name.trim(), slug: slug.trim(), permissions: selected });
    } catch (err) {
      const detail = isAxiosError<{ message?: string }>(err) ? err.response?.data?.message : null;
      setError(detail || 'Не удалось сохранить роль.');
    } finally {
      setSaving(false);
    }
  };

  const disabled = readOnly || saving;

  return (
    <div className={`operator-template-form${saving ? ' operator-template-form--saving' : ''}`} aria-busy={saving}>
      {error && <p className="field-error">{error}</p>}

      <div className="edit-fields">
        <label className={`edit-field${invalid('name')}`}>
          <span>Название *</span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} disabled={disabled} />
        </label>

        <label className={`edit-field${invalid('slug')}`}>
          <span>Идентификатор *</span>
          {/* Недопустимые символы не вводятся вовсе — так понятнее, чем
              ошибка после сохранения. Проверка при сабмите всё равно
              остаётся: значение может прийти вставкой из буфера. */}
          <input
            type="text"
            value={slug}
            placeholder="manager"
            onChange={(e) => setSlug(e.target.value.replace(/[^A-Za-z0-9._-]/g, ''))}
            disabled={disabled}
          />
          <span className="edit-field__hint">Только латиница, цифры, точка, дефис и подчёркивание.</span>
        </label>
      </div>

      <div className="permissions-block">
        <h2 className="detail__subtitle">Разрешения и привилегии</h2>
        <p className="edit-field__hint">
          Отметьте, какие задачи и операции доступны этой роли.
        </p>

      {groups === null ? (
        <div className="panel-loading">
          <Spinner />
        </div>
      ) : (
        <div className="permissions">
          {groups.map((group) => {
            const slugs = group.permissions.map((p) => p.slug);
            const checkedCount = slugs.filter((s) => selected.includes(s)).length;
            const allChecked = checkedCount === slugs.length;

            return (
              <section
                className={`permissions__group${checkedCount > 0 ? ' permissions__group--active' : ''}`}
                key={group.title}
              >
                <header className="permissions__head">
                  <span className="permissions__icon">
                    <Icon name={GROUP_ICONS[group.title] ?? 'lock'} size={16} />
                  </span>
                  <span className="permissions__title">{group.title}</span>
                  <span className="permissions__count">
                    {checkedCount} из {slugs.length}
                  </span>
                </header>

                <label className="permissions__all">
                  <input
                    type="checkbox"
                    checked={allChecked}
                    disabled={disabled}
                    onChange={(e) => toggleGroup(group, e.target.checked)}
                  />
                  <span>Выбрать все</span>
                </label>

                <ul className="permissions__list">
                  {group.permissions.map((permission) => {
                    const checked = selected.includes(permission.slug);
                    return (
                      <li key={permission.slug}>
                        <label className={`permissions__item${checked ? ' permissions__item--on' : ''}`}>
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={disabled}
                            onChange={() => toggle(permission.slug)}
                          />
                          <span className="permissions__label">{permission.description}</span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
        </div>
      )}
      </div>

      <div className="operator-template-form__actions">
        <button type="button" className="btn btn--secondary" onClick={onCancel} disabled={saving}>
          {readOnly ? 'Назад' : 'Отмена'}
        </button>
        {!readOnly && (
          <button type="button" className="btn btn--primary" onClick={submit} disabled={saving}>
            Сохранить
          </button>
        )}
      </div>
    </div>
  );
}
